import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ArrowRight, Check, Sparkles, Mail, Download, Heart } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
// MailerLite group ID for workbook lead-magnet subscribers.
// Falls back to default group if not set in backend env.
const WORKBOOK_GROUP_ID = "workbook_lead_magnet";

// Context-aware microcopy: shows above the form when a user arrives from a
// specific intent (e.g. "Notify me when the book ships"). Keeps the promise
// honest — they came for X, we still deliver X, plus the workbook bonus.
const CONTEXT_BANNERS = {
  "book-waitlist": {
    eyebrow: "✦ The Book · Paperback waitlist",
    note: "You'll be first to know the day \"I GET TO\" (paperback) ships — and the workbook below is yours today to start the practice.",
  },
  "card-pulls": { eyebrow: "✦ From the Card Pulls", note: "Take the practice with you — the workbook is the perfect companion." },
  "card-draw": { eyebrow: "✦ From the Card Draw", note: "Take the practice with you — the workbook is the perfect companion." },
  "yes-no-wait": { eyebrow: "✦ From Yes. No. Wait.", note: "Loved the cards? The workbook goes deeper." },
  "about": { eyebrow: "✦ From Temple's Story", note: "Start the same practice that pulled Temple out of the muck." },
  "blog": { eyebrow: "✦ From the Blog", note: "Make it a practice, not just a read." },
  "boundary-landing": { eyebrow: "✦ Boundaries waitlist", note: "While the course is being built, the workbook teaches the foundation." },
};

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  tealLight: "#25a1c2",
  cream: "#efeae7",
  gold: "#d4af6a",
  mint: "#5EEAD4",
};

export default function WorkbookOptin() {
  const [searchParams] = useSearchParams();
  const fromSource = searchParams.get("from") || "";
  const context = CONTEXT_BANNERS[fromSource];

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    // GA4 — log the workbook landing view with UTM context (Page View is auto, this is a custom event)
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "workbook_view", {
        from: fromSource || "(direct)",
        utm_source: searchParams.get("utm_source") || "(none)",
        utm_medium: searchParams.get("utm_medium") || "(none)",
        utm_campaign: searchParams.get("utm_campaign") || "(none)",
      });
    }
  }, [fromSource, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        firstName: firstName || "Friend",
        email: email.trim().toLowerCase(),
        // NOTE: do NOT send group_id from the frontend — backend uses MAILERLITE_DEFAULT_GROUP_ID
        // (the real numeric ID for the 52-week journey). Sending a slug here would
        // silently fail to add the subscriber to the right group.
        source: fromSource ? `workbook-optin-${fromSource}` : "workbook-optin-page",
        utm_source: searchParams.get("utm_source") || null,
        utm_medium: searchParams.get("utm_medium") || null,
        utm_campaign: searchParams.get("utm_campaign") || null,
        utm_content: searchParams.get("utm_content") || null,
        utm_term: searchParams.get("utm_term") || null,
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
      });

      // GA4 — fire conversion event so Temple can measure signups by source in Reports
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "workbook_signup", {
          method: "email_form",
          from: fromSource || "(direct)",
          utm_source: searchParams.get("utm_source") || "(direct)",
          utm_medium: searchParams.get("utm_medium") || "(none)",
          utm_campaign: searchParams.get("utm_campaign") || "(none)",
          value: 0,
          currency: "USD",
        });
        // Also fire standard 'generate_lead' so it shows in Reports → Events automatically
        window.gtag("event", "generate_lead", { currency: "USD", value: 0 });
      }
      // Meta Pixel — Lead conversion (safe no-op until pixel base loads)
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "I GET TO Workbook", content_category: "lead-magnet", value: 0, currency: "USD" });
      }
      // Mark this device as a returning subscriber so homepage swaps to the "welcome back" hero
      try { localStorage.setItem("mbsh_subscribed", "true"); } catch (_) { /* ignore */ 
      }

      // Redirect to success page where the PDF auto-downloads
      window.location.href = "/workbook-success";
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail || "Something hiccuped. Try once more, Lovely."
      );
    }
  };

  return (
    <div className="min-h-screen" style={{ background: BRAND.cream }} data-testid="workbook-optin-page">
      <Helmet>
        <title>Free I GET TO Workbook — Mind Body Spirit HYGIENE™ Method · Temple Franklin</title>
        <meta
          name="description"
          content="Free 12-page Mind Body Spirit HYGIENE™ workbook by Temple Franklin. Learn the simple tool that helps you rewire how you talk to yourself. Instant download + 365-day affirmation calendar bonus."
        />
        <meta name="keywords" content="I GET TO workbook, free workbook, Mind Body Spirit HYGIENE™, Temple Franklin, neuroplasticity, rewire your brain, self-talk, affirmations, free download, lead magnet" />
        <link rel="canonical" href="https://mindbodyspirithygienetools.com/workbook" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Free I GET TO Workbook — A 12-Page Practice for Rewiring Your Brain" />
        <meta property="og:description" content="Your brain is not stuck, Lovely. Download the free I GET TO workbook + a bonus 365-day affirmation calendar. Instant PDF." />
        <meta property="og:url" content="https://mindbodyspirithygienetools.com/workbook" />
      </Helmet>

      {/* ─── Minimal header (no nav distraction) ─── */}
      <header className="px-6 py-5 border-b" style={{ borderColor: "rgba(4,47,46,0.08)" }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-lg tracking-tight" style={{ color: BRAND.deep }} data-testid="workbook-back-home">
            Mind Body Spirit HYGIENE<sup className="text-xs">™</sup>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/build-your-own" className="font-sans text-xs tracking-wider uppercase" style={{ color: BRAND.teal }} data-testid="workbook-nav-build-your-own">
              Build Your Own
            </Link>
            <Link to="/free" className="font-sans text-xs tracking-wider uppercase" style={{ color: BRAND.teal }} data-testid="workbook-free-resources-link">
              All Free Resources →
            </Link>
          </div>
        </div>
      </header>

      {/* ─── HERO + OPT-IN ─── */}
      <section className="px-6 py-12 md:py-20" data-testid="workbook-hero">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* LEFT — Copy + Form */}
          <div>
            {context && (
              <div
                className="mb-5 p-4 rounded-xl border-l-4"
                style={{
                  background: "rgba(212,175,106,0.12)",
                  borderColor: BRAND.gold,
                }}
                data-testid="workbook-context-banner"
              >
                <p
                  className="font-sans text-[10px] tracking-[0.3em] uppercase font-bold mb-1.5"
                  style={{ color: BRAND.gold }}
                >
                  {context.eyebrow}
                </p>
                <p
                  className="font-serif text-sm md:text-base italic leading-relaxed"
                  style={{ color: BRAND.deep }}
                >
                  {context.note}
                </p>
              </div>
            )}
            <p
              className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-4"
              style={{ color: BRAND.gold }}
              data-testid="workbook-eyebrow"
            >
              ✦ Free printable workbook
            </p>
            <h1
              className="font-serif tracking-tighter leading-[1.05] mb-5"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)", color: BRAND.deep }}
              data-testid="workbook-headline"
            >
              Your brain is not stuck, Lovely&nbsp;— and two small words can prove it.
            </h1>
            <p
              className="font-serif text-lg md:text-xl leading-relaxed mb-7"
              style={{ color: BRAND.teal }}
              data-testid="workbook-subhead"
            >
              Download <em>I GET TO</em> — a 12-page Mind Body Spirit HYGIENE™ practice. You&rsquo;ll learn the simple tool that helps you rewire the way you talk to yourself — one true word at a time.
            </p>

            {status !== "success" && (
              <form onSubmit={handleSubmit} className="space-y-3 mb-3" data-testid="workbook-optin-form">
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-5 py-4 rounded-full font-sans text-base border-2 focus:outline-none transition-colors"
                  style={{ borderColor: "rgba(6,143,162,0.25)", background: "white", color: BRAND.deep }}
                  data-testid="workbook-firstname-input"
                />
                <input
                  type="email"
                  placeholder="Your best email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-5 py-4 rounded-full font-sans text-base border-2 focus:outline-none transition-colors"
                  style={{ borderColor: "rgba(6,143,162,0.25)", background: "white", color: BRAND.deep }}
                  data-testid="workbook-email-input"
                />
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide transition-all hover:-translate-y-0.5 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: BRAND.teal, color: "white" }}
                  data-testid="workbook-submit-btn"
                >
                  {status === "submitting" ? (
                    "Sending…"
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send me the free workbook
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                {errorMsg && (
                  <p className="font-sans text-sm pt-2" style={{ color: "#b91c1c" }} data-testid="workbook-error-msg">
                    {errorMsg}
                  </p>
                )}
              </form>
            )}

            <p
              className="font-sans text-xs leading-relaxed"
              style={{ color: "#1E2A29" }}
              data-testid="workbook-microcopy"
            >
              Instant download, straight to your inbox — plus a surprise 365-day affirmation calendar and a year of weekly notes to keep you company in the messy middle. Unsubscribe anytime.
            </p>
          </div>

          {/* RIGHT — Workbook cover visual */}
          <div className="flex justify-center md:justify-end">
            <div
              className="relative w-full max-w-sm aspect-[6/8] rounded-md shadow-2xl overflow-hidden flex flex-col items-center justify-center text-center px-8"
              style={{
                background: `linear-gradient(155deg, ${BRAND.deep} 0%, ${BRAND.teal} 60%, ${BRAND.tealLight} 100%)`,
              }}
              data-testid="workbook-cover-visual"
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at 70% 30%, rgba(212,175,106,0.22) 0%, transparent 55%)`,
                }}
              />
              <p
                className="relative font-sans text-[9px] tracking-[0.4em] uppercase font-bold mb-4"
                style={{ color: BRAND.gold }}
              >
                12-page workbook · free
              </p>
              <h2 className="relative font-serif text-white tracking-tighter leading-none mb-3" style={{ fontSize: "clamp(2.75rem, 6vw, 4.25rem)" }}>
                I GET TO
              </h2>
              <p className="relative font-serif italic text-base md:text-lg leading-snug mb-5" style={{ color: "rgba(255,255,255,0.85)" }}>
                A Mind Body Spirit HYGIENE™ Practice for Rewiring Your Brain — One True Word at a Time
              </p>
              <p className="relative font-sans text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: BRAND.mint }}>
                By Temple Franklin
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── What's inside ─── */}
      <section className="px-6 py-14 md:py-20" style={{ background: "white" }} data-testid="workbook-whats-inside">
        <div className="max-w-4xl mx-auto">
          <p
            className="font-sans text-[10px] tracking-[0.35em] uppercase font-bold mb-3 text-center"
            style={{ color: BRAND.gold }}
          >
            What&rsquo;s inside the 12 pages
          </p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-center mb-10" style={{ color: BRAND.deep }}>
            One simple tool. Your turn.
          </h2>
          <div className="grid md:grid-cols-2 gap-4 md:gap-5">
            {[
              "Your Brain Is Not Stuck",
              "What Your Words Do to Your Brain",
              "The I GET TO Method",
              "A Practice, Not a Mood",
              "What You\u2019ll Start to Notice",
              "Now, Your Turn",
              "Your 7-Day I GET TO Practice",
              "Welcome Home",
              "The Science, a Little Further",
            ].map((section, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-xl"
                style={{ background: BRAND.cream }}
                data-testid={`workbook-section-${i}`}
              >
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: BRAND.teal }} />
                <span className="font-serif text-base md:text-lg" style={{ color: BRAND.deep }}>
                  {section}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Trust strip ─── */}
      <section className="px-6 py-10 md:py-14" data-testid="workbook-trust">
        <div className="max-w-3xl mx-auto text-center">
          <Heart className="w-6 h-6 mx-auto mb-3" style={{ color: BRAND.gold }} />
          <p className="font-serif italic text-lg md:text-2xl leading-relaxed" style={{ color: BRAND.deep }}>
            &ldquo;I&rsquo;m Temple Franklin — founder of the Mind Body Spirit HYGIENE™ Method. This workbook is the same practice that pulled me out of the muck. It&rsquo;s yours. Free. Welcome home.&rdquo;
          </p>
        </div>
      </section>

      {/* ─── Footer ─── */}
    </div>
  );
}
